<script lang="ts" setup generic="TValue extends AcceptableInputValue">
import type { Model } from '@atabix/core'
import type { AcceptableInputValue } from 'reka-ui'

import type { Inputable } from '../../../../lib/types/Inputable'
import type { Testable } from '../../../../lib/types/Testable'
import AMultiSelect from '../../AMultiSelect/AMultiSelect.vue'
import { FormControl, FormField, FormItem } from '../../form'
import AFormMessage from '../../form/FormMessage.vue'
import { Label } from '../../label'
import ASingleSelect from '../../select/ASingleSelect.vue'
import AModelSelect from '../AModelSelect/AModelSelect.vue'

export type AObjectSelectItem<TItem = unknown> = { label: string; value: string; item?: TItem }

export type AStringSelectItem = string
// TODO: Find a good way to merge these with the other props
// (AMultiSelectProperties | AModelSelectProperties)
export type ASelectInputProperties = Testable &
  Inputable & {
    items?: AObjectSelectItem[] | AStringSelectItem[]
    type?: 'select'
    /** @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#token_list_tokens */
    autocomplete?: false
    label?: string
    ariaLabel?: string
    placeholder?: string
    disabled?: boolean
    model?: Model
    multiple?: boolean
  }

const properties = withDefaults(defineProps<ASelectInputProperties>(), {
  id: undefined,
  type: 'select',
  items: () => [],
  class: undefined,
  name: undefined,
  label: undefined,
  ariaLabel: undefined,
  placeholder: undefined,
  model: undefined,
})

const modelValue = defineModel<TValue>({ required: false })
</script>

<template>
  <FormField v-slot="{ componentField }" v-model="modelValue" :name="name || ''">
    <FormItem>
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <Label v-if="label !== undefined" class="mt-2" :for="id">{{ label }} {{ required ? '*' : '' }}</Label>
      <FormControl>
        <!-- AModelSelect -->
        <template v-if="model">
          <AModelSelect v-bind="{ ...componentField, ...properties, ...$attrs }" v-model="modelValue" :multiple="multiple || false">
            <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
            <template #empty><slot name="empty" /></template>
          </AModelSelect>
        </template>

        <!-- ASingleSelect | AMultiSelect -->
        <template v-else>
          <ASingleSelect v-if="!multiple" v-bind="{ ...componentField, ...properties, ...$attrs }" v-model="modelValue">
            <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
          </ASingleSelect>
          <AMultiSelect v-if="multiple" v-bind="{ ...componentField, ...properties, ...$attrs }" v-model="modelValue" :items="items">
            <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
            <template #empty><slot name="empty" /></template>
          </AMultiSelect>
        </template>
      </FormControl>
      <AFormMessage />
    </FormItem>
  </FormField>
</template>

<style lang="scss">
.atabix-select-input {
  //
}
</style>
